import React, {useState, useEffect, useMemo} from "react"
import {Link, useNavigate} from "react-router-dom";
// MUI
import {Box, Button, InputBase, Skeleton, Typography} from "@mui/material";
import {Add, SearchRounded, SellOutlined} from "@mui/icons-material";
// Data
import {useUser} from "@/context/UserProvider";
import {useData} from "@/context/PackagingDataProvider";
// Components
import {DeleteAnyDialog} from "@/components/UI";
import {PageHeader} from "@/components/Layout";
import BrandCard from "@/components/Brands/BrandCard";
import {packagingTokens as tk} from "@/theme/packagingTokens";
import {indexPropagatedFilters} from "@/utils/filterPresets";
// Translation
import {useTranslation} from "react-i18next";
import {usePermissions} from "@/context/PermissionsProvider";

export default function BrandsTab() {

    const {t} = useTranslation(["brands", "common", "ranges"])
    const navigate = useNavigate()
    const {organisation} = useUser()
    const {isAccountRole} = usePermissions()
    const {
        brands, getBrands,
        ranges, getRanges,
        commonFilters,
        deleteAny,
        propagatedFilters,
    } = useData()

    const lockEdition = isAccountRole(["analyst"])

    // Dialogs
    const [deletion, setDeletion] = useState<any>(null)
    const [deletionType, setDeletionType] = useState<"brand" | "range" | null>(null)
    const [openDeleteDialog, setOpenDeleteDialog] = useState<boolean>(false)
    // Client-side search
    const [search, setSearch] = useState<string>("")
    // Formatted brands (each brand + its ranges)
    const [classification, setClassification] = useState<any>(null)

    useEffect(() => {
        if (!!organisation && !brands) {
            if (!commonFilters.organisation) {
                getBrands()
                getRanges()
            } else {
                getBrands(commonFilters.organisation)
                getRanges(commonFilters.organisation)
            }
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [organisation])

    // The organisation scope now lives in the sidebar (commonFilters). Refetch
    // both brands and ranges whenever it changes.
    useEffect(() => {
        if (!organisation) return
        if (!!commonFilters.organisation) {
            getBrands(commonFilters.organisation)
            getRanges(commonFilters.organisation)
        } else {
            getBrands()
            getRanges()
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [commonFilters.organisation])

    useEffect(() => {
        if (!!brands && !!ranges) sortBrandsAndRanges()
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [brands, ranges])

    const sortBrandsAndRanges = () => {
        let arr = [...brands]
        if (!!arr.length) {
            arr.forEach((brand: any, i: number) => {
                arr[i].ranges = ranges.filter((r: any) => r.brand.id === brand.id)
            })
        } else arr = []
        setClassification(arr)
    }

    // Client-side search across brand names and their range names.
    const filtered = useMemo(() => {
        if (!classification) return null
        const q = search.trim().toLowerCase()
        if (!q) return classification
        return classification.filter((brand: any) =>
            brand.name?.toLowerCase().includes(q) ||
            (brand.ranges ?? []).some((r: any) => r.name?.toLowerCase().includes(q))
        )
    }, [classification, search])

    // Propagated filters, looked up per card. Indexed by id, so the
    // organisation scope of the list above does not matter.
    const propagated = useMemo(() => indexPropagatedFilters(propagatedFilters ?? null), [propagatedFilters])

    // Handlers
    const handleEditBrand = (brand: any) => navigate("/brands/edit", {state: {...brand}})
    const handleEditRange = (range: any) => navigate("/ranges/edit", {state: {...range}})
    const handleAddRange = (brand: any) => navigate(`/ranges/create?brandid=${brand.id}`)

    const handleDelete = (type: "brand" | "range", item: any) => {
        setDeletionType(type)
        setDeletion(item)
        setOpenDeleteDialog(true)
    }

    const deleteElement = () => {
        if (deletionType === "brand") deleteAny(deletion, "brand").then((d: any) => {if (!!d) getBrands(commonFilters.organisation || undefined)})
        else if (deletionType === "range") deleteAny(deletion, "range").then((d: any) => {if (!!d) getRanges(commonFilters.organisation || undefined)})
        setOpenDeleteDialog(false)
        setDeletion(null)
    }

    const btnSecondary = {
        textTransform: "none", fontSize: "13.5px", fontWeight: 600, borderRadius: tk.radius.sm, px: 2,
        color: tk.color.textPrimary, border: `1px solid ${tk.color.border}`, backgroundColor: tk.color.surface,
        "&:hover": {backgroundColor: tk.color.bg, borderColor: tk.color.borderStrong},
    }

    return (
        <Box>
            <PageHeader
                title={t("common:brands_ranges")}
                subtitle={t("brands_subtitle", {defaultValue: "Organise products under brands and ranges"})}
                actions={
                    <>
                        <Button
                            component={Link} to={"/ranges/create"} disabled={lockEdition} data-step-target={"create-range"}
                            startIcon={<Add sx={{fontSize: 16}} />} sx={btnSecondary}
                        >
                            {t("range_create")}
                        </Button>
                        <Button
                            variant={"contained"} disableElevation component={Link} to={"/brands/create"} disabled={lockEdition} data-step-target={"create-brand"}
                            startIcon={<Add sx={{fontSize: 16}} />}
                            sx={{textTransform: "none", fontSize: "13.5px", fontWeight: 600, borderRadius: tk.radius.sm, px: 2}}
                        >
                            {t("brand_create")}
                        </Button>
                    </>
                }
            />

            {/* Filter row: search on the left, export opposite — kept away from
                the header's creation actions so it doesn't read as one. */}
            <Box sx={{display: "flex", alignItems: "center", justifyContent: "space-between", gap: 1, mb: 2.5}}>
                <Box
                    sx={{
                        display: "flex", alignItems: "center", gap: 1,
                        flex: 1, maxWidth: 320, px: 1.5, py: 0.75,
                        border: `1px solid ${tk.color.border}`, borderRadius: tk.radius.sm,
                        backgroundColor: tk.color.surface,
                        "&:focus-within": {borderColor: tk.color.borderStrong},
                    }}
                >
                    <SearchRounded sx={{fontSize: 18, color: tk.color.textTertiary}} />
                    <InputBase
                        fullWidth
                        value={search}
                        onChange={(e) => setSearch(e.target.value)}
                        placeholder={t("search_brands_ranges", {defaultValue: "Search brands or ranges..."})}
                        sx={{fontSize: "13px"}}
                    />
                </Box>
                {/* Spreadsheet export moved to the Batch management page (Tools). */}
            </Box>

            {/* Grid / loading / empty */}
            {filtered === null ? (
                <Box sx={{display: "grid", gridTemplateColumns: {xs: "1fr", sm: "1fr 1fr", lg: "repeat(3, 1fr)"}, gap: 2}}>
                    {Array.from({length: 6}).map((_, i) => (
                        <Skeleton key={i} variant={"rounded"} height={190} sx={{borderRadius: tk.radius.card}} />
                    ))}
                </Box>
            ) : filtered.length === 0 ? (
                <Box
                    sx={{
                        textAlign: "center", py: 8, px: 3,
                        border: `1px dashed ${tk.color.border}`, borderRadius: tk.radius.card,
                        backgroundColor: tk.color.surface,
                    }}
                >
                    <SellOutlined sx={{fontSize: 32, color: tk.color.textTertiary, mb: 1}} />
                    <Typography sx={{fontSize: "15px", fontWeight: 700, mb: 0.5}}>
                        {search ? t("no_results", {defaultValue: "No matches"}) : t("common:no_brands_ranges")}
                    </Typography>
                    {!search && (
                        <Typography sx={{fontSize: "13px", color: tk.color.textSecondary}}>
                            {t("brands_empty_hint", {defaultValue: "Create your first brand to get started."})}
                        </Typography>
                    )}
                </Box>
            ) : (
                <Box sx={{display: "grid", gridTemplateColumns: {xs: "1fr", sm: "1fr 1fr", lg: "repeat(3, 1fr)"}, gap: 2}}>
                    {filtered.map((brand: any) => (
                        <BrandCard
                            key={brand.id}
                            brand={brand}
                            lockEdition={lockEdition}
                            onEditBrand={handleEditBrand}
                            onDeleteBrand={(b) => handleDelete("brand", b)}
                            onEditRange={handleEditRange}
                            onDeleteRange={(r) => handleDelete("range", r)}
                            onAddRange={handleAddRange}
                            propagatedFilters={propagated.byBrand[brand.id]}
                            rangePropagatedFilters={propagated.byRange}
                            t={t}
                        />
                    ))}
                </Box>
            )}


            <DeleteAnyDialog
                open={openDeleteDialog}
                onClose={() => setOpenDeleteDialog(false)}
                onDelete={() => deleteElement()}
                assetName={deletion?.name}
                // The dialog serves both card-level deletes: match the copy to
                // what is actually being deleted (range chip ✕ vs brand kebab).
                text={deletionType === "range" ? t("ranges:ranges_delete_alert") : t("delete_brand_alert")}
                {...{t}}
            />
        </Box>
    )
}
